import { AdminProvider, ProductUndoProvider, Toaster, TooltipProvider } from "@aix/ui"
import * as Sentry from "@sentry/react"
import { useAtom, useAtomValue } from "jotai"
import React, { useEffect, useMemo } from "react"
import { CalendarLayoutPrivider } from "~/context/CalendarLayoutProvider"
import { FreemiumModalProvider } from "~/context/FreemiumModalContext"
import { useAuthSession } from "~/hooks/useAuthSession"
import { useGlobalQuery } from "~/hooks/useGlobalQuery"
import { useGlobalShortcuts } from "~/hooks/useGlobalShortcuts"
import { demoModeAtom } from "~/context/jotai"
import { isAdminUserFE, superuserJT } from "~/utils/admin"
import { api } from "~/utils/api"
import PostHogClientProvider from "~/utils/posthog"
import { usePH } from "~/utils/posthogClient"
import VidmaxDndProvider from "./VidmaxDndProvider"

export function ClientProviders(props: { children: React.ReactNode }) {
  useGlobalQuery()
  useGlobalShortcuts()

  return (
    <Sentry.ErrorBoundary
      fallback={() => (
        <div className="flex items-center justify-center min-h-screen flex-col gap-4">
          <p className="text-lg font-medium">Something went wrong. Please refresh the page.</p>
          <button
            className="px-4 py-2 bg-primary text-primary-foreground rounded-md"
            onClick={() => window.location.reload()}
          >
            Refresh
          </button>
        </div>
      )}
    >
      <PostHogClientProvider>
        <TooltipProvider delayDuration={150}>
          <FreemiumModalProvider>
            <CalendarLayoutPrivider>
              <VidmaxDndProvider>
                <ProductUndoProvider>
                  <ChildrenWrapper>{props.children}</ChildrenWrapper>
                </ProductUndoProvider>
              </VidmaxDndProvider>
            </CalendarLayoutPrivider>
          </FreemiumModalProvider>
          <Toaster />
        </TooltipProvider>
      </PostHogClientProvider>
    </Sentry.ErrorBoundary>
  )
}

const ChildrenWrapper = (props: { children: React.ReactNode }) => {
  const { identify } = usePH()
  const { session } = useAuthSession()
  const utils = api.useUtils()

  // Prefetch pricing products on app load so pricing page loads instantly
  useEffect(() => {
    void utils.subscriptions.getProducts.prefetch()
  }, [])

  // Admin context - use query to properly subscribe to changes (fixes HMR losing admin state)
  const { data: user } = api.users.getUserWithTeam.useQuery(undefined, {
    enabled: !!session?.user?.id,
    staleTime: Infinity, // Don't refetch unless explicitly invalidated
  })
  const showSuperuser = useAtomValue(superuserJT)
  const [demoMode] = useAtom(demoModeAtom)
  const isAdmin = useMemo(() => !demoMode && isAdminUserFE(user) && showSuperuser, [user, showSuperuser, demoMode])

  useEffect(() => {
    if (session?.user?.id) {
      identify(session.user.id)
    }
  }, [identify, session?.user?.id])

  return <AdminProvider isAdmin={!!isAdmin}>{props.children}</AdminProvider>
}
